import type { FC, KeyboardEventHandler, MouseEvent } from "react";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import { FormField } from "@ui/components/Form/FormField";
import { OtpInput } from "@ui/components/OtpInput/OtpInput";
import { useCounters } from "@ui/hooks/useCounters/useCounters";
import { useTranslations } from "@ui/providers/useTranslations";
import { secondsToMs } from "@ui/utils/secondsToMs/secondsToMs";
import classNames from "classnames";

import { Button } from "@jti/core-components/components/Button";

import styles from "./EmailOtp.module.scss";

type EmailOtpProps = {
  otpName: string;
  time: number;
  onResendCode(): void;
  showBorder?: boolean;
  className?: string;
  classNameText?: string;
  onSubmit?(code?: string): void;
  autocompleteType?: string;
  isOtpNumeric?: boolean;
  isAutomaticSubmitEnabled?: boolean;
  isDisabled?: boolean;
  isCodeActive?: boolean;
};

const OTP_LENGTH = 4;

export const EmailOtp: FC<EmailOtpProps> = ({
  otpName,
  time,
  showBorder = true,
  onResendCode,
  onSubmit,
  autocompleteType = "off",
  isOtpNumeric = false,
  isAutomaticSubmitEnabled = false,
  isDisabled = false,
  isCodeActive,
  ...props
}) => {
  const { __ } = useTranslations();
  const { watch } = useFormContext();
  const otpValue = watch(otpName);
  const otpUuidFieldName = otpName.endsWith("_code") ? otpName.replace(/_code$/, "_uuid") : null;
  const otpUuidValue = otpUuidFieldName ? watch(otpUuidFieldName) : undefined;
  const isCodeActiveResolved =
    typeof isCodeActive === "boolean" ? isCodeActive : otpUuidFieldName ? Boolean(otpUuidValue) : true;
  const isOtpFilled = otpValue?.length === OTP_LENGTH;
  const { remainingTime, resetTimer, startCounter } = useCounters(time);
  const borderClassNames = classNames(styles.border, props.className);
  const textClassNames = classNames(styles.text, props.classNameText);
  const prevOtpValue = useRef("");

  useEffect(() => {
    if (!isCodeActiveResolved) {
      return;
    }

    startCounter();
  }, [isCodeActiveResolved, startCounter]);

  const handleResendCode = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      if (isDisabled || !isCodeActiveResolved) {
        return;
      }
      onResendCode();
      resetTimer();
    },
    [isDisabled, isCodeActiveResolved, onResendCode, resetTimer]
  );

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (isDisabled || !isCodeActiveResolved) {
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      onSubmit?.();
    }
  };

  useEffect(() => {
    if (isDisabled || !isCodeActiveResolved) {
      prevOtpValue.current = otpValue || "";
      return;
    }

    if (isAutomaticSubmitEnabled && isOtpFilled && prevOtpValue.current.length !== OTP_LENGTH) {
      onSubmit?.(otpValue);
    }

    prevOtpValue.current = otpValue || "";
  }, [isOtpFilled, isAutomaticSubmitEnabled, isDisabled, isCodeActiveResolved, onSubmit, otpValue]);

  return (
    <div className={borderClassNames}>
      {showBorder && <div className={styles.text}>{__("auth.registration.login_credentials.email_verify.text")}</div>}
      <div data-testid="email-otp-wrapper" onKeyDown={handleKeyDown}>
        <FormField
          name={otpName}
          render={({ field }) => (
            <OtpInput
              {...field}
              data-testid="otpInput"
              inputStyle={`${styles.inputStyle}`}
              autocompleteType={autocompleteType}
              isInputNum={isOtpNumeric}
              isDisabled={isDisabled}
            />
          )}
          validation={{
            required: __("auth.registration.otp.validation.field_required"),
            minLength: {
              value: OTP_LENGTH,
              message: __("auth.registration.otp.validation.field_min_length"),
            },
          }}
        />
      </div>
      {isCodeActiveResolved && (
        <div className={textClassNames}>
          {remainingTime ? (
            <>
              <span>{__("auth.registration.otp.email.sent")}</span>
              <div className={styles.timer}>
                {__("auth.registration.login_credentials.email_verify.resend_text.request_another")}
                <span data-testid="otp-timeout-counter">{secondsToMs(remainingTime)}</span>
              </div>
            </>
          ) : (
            <>
              <span className={styles.requestNewCode}>{__("auth.registration.otp.request_new_code_text")}</span>
              <Button
                size="lg"
                data-testid="resendCode"
                variant="text"
                onClick={handleResendCode}
                disabled={isDisabled}
              >
                {__("auth.registration.otp.request_new_code_code")}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
